import { useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  /** Full-text label — always rendered, even when the trigger itself is
   * icon-only (condensed tab-shell mode). */
  label: string;
  children: ReactNode;
}

/**
 * Minimal portaled tooltip (no @astrana/ui / Radix dependency in this host —
 * see repo boundary against new deps). Shows `label` on hover OR focus of the
 * wrapped trigger, so it doubles as the visible affordance for sighted mouse
 * users AND keyboard users tabbing through the icon-condensed tab bar.
 *
 * Per this repo's CLAUDE.md overlay convention, the bubble is portaled to
 * `document.body` (never rendered inline) — an ancestor `@container`
 * (`container-type: inline-size`, used by the tab shell nav itself) would
 * otherwise promote the inline subtree to its own compositing layer and
 * re-rasterize text around it (see SwitchPatientModal precedent).
 *
 * The wrapping `<span>` uses `display: contents` so it never participates in
 * the parent flex row's layout — the trigger element is the only visible box.
 */
export function Tooltip({ label, children }: TooltipProps) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  const show = () => {
    // The wrapper is `display: contents` (no box), so its own
    // getBoundingClientRect() is empty (0,0) — which anchored the bubble to the
    // far-left of the viewport. Measure the actual trigger element (the wrapper's
    // first child) instead.
    const el = anchorRef.current?.firstElementChild ?? anchorRef.current;
    const rect = el?.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) return;
    setCoords({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
  };
  const hide = () => setCoords(null);

  return (
    <span
      ref={anchorRef}
      className="contents"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={coords ? id : undefined}
    >
      {children}
      {coords &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            className="fixed z-50 -translate-x-1/2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md pointer-events-none"
            style={{ top: coords.top, left: coords.left }}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
